// LanguagesTab.js
import React, { useState, useEffect } from "react";
import {
  Tab,
  Header,
  Form,
  Accordion,
  Icon,
  Input,
  TextArea,
  Button,
  Label,
  Segment,
  Dropdown,
} from "semantic-ui-react";

/**
 * 1) Extract all unique text from questions, factors, factoAggregate,
 *    including blocks for "consent" or "information" items.
 */
function extractUniqueStrings(questions = [], factors = [], factoAggregate = []) {
  const uniqueSet = new Set();

  // Helper to safely add text if it's defined
  const addIfDefined = (text) => {
    if (typeof text !== "undefined" && text !== null) {
      uniqueSet.add(text);
    }
  };

  // Check blocks array (used for consent, information, or other items)
  const processBlocks = (blocks) => {
    if (!Array.isArray(blocks)) return;
    blocks.forEach((block) => {
      if (block.content) {
        addIfDefined(block.content);
      }
      if (block.label) {
        addIfDefined(block.label);
      }
    });
  };

  // A) Questions (including "consent", "information", etc.)
  questions.forEach((q) => {
    const p = q.properties || {};

    // Common question properties: label, lowLabel, highLabel
    addIfDefined(p.label);
    addIfDefined(p.lowLabel);
    addIfDefined(p.highLabel);

    // options
    if (Array.isArray(p.options)) {
      p.options.forEach((opt) => addIfDefined(opt));
    }

    // conditionals
    if (q.conditionals?.conditionals) {
      q.conditionals.conditionals.forEach((cond) => {
        addIfDefined(cond.followupText);
        addIfDefined(cond.aiPrompt);
      });
    }

    // Blocks (e.g. for consent or information items)
    if (Array.isArray(p.blocks)) {
      processBlocks(p.blocks);
    }
  });

  // B) Factors
  factors.forEach((factor) => {
    addIfDefined(factor.title);
    addIfDefined(factor.description);

    if (Array.isArray(factor.followUps)) {
      factor.followUps.forEach((fu) => {
        addIfDefined(fu.aiPrompt);
      });
    }
  });

  // C) factoAggregate
  factoAggregate.forEach((agg) => {
    addIfDefined(agg.followUpQuestion);
    addIfDefined(agg.aiPrompt);
  });

  // Return an array
  return Array.from(uniqueSet);
}

/* ==========================================================
   2) GlobalStringsTab
   - Displays each unique snippet in a list, so you only
     translate repeated text once.
   ========================================================== */
function GlobalStringsTab({
  allStrings,
  languages, // array of language codes (["ES", "FR", ...])
  globalDictionary,
  setGlobalDictionary,
}) {
  /**
   * If the user changes a translation, we update the parent's globalDictionary.
   */
  const handleTranslationChange = (originalText, lang, newValue) => {
    setGlobalDictionary((prev) => {
      const updated = { ...prev };
      if (!updated[originalText]) {
        updated[originalText] = {};
      }
      updated[originalText][lang] = newValue;
      return updated;
    });
  };

  // If no languages, show a message
  if (!languages.length) {
    return <p>Please add a language to begin translating.</p>;
  }

  // If no strings at all, show a message
  if (!allStrings.length) {
    return <p>No text found to translate.</p>;
  }

  return (
    <div style={{ padding: 20 }}>
      <Header as="h3">Global Strings Dictionary</Header>
      <p>
        These are all the unique text snippets found in your questions, factors,
        and aggregates. Translating them here ensures repeated text is
        translated only once.
      </p>

      <Form>
        {allStrings.map((originalText) => {
          // For display, handle empty original text
          const displayOriginal =
            originalText?.trim() === "" ? "(empty)" : originalText;

          return (
            <Segment key={originalText || "empty"}>
              <strong>Original:</strong> <em>{displayOriginal}</em>
              <div style={{ marginTop: 10 }}>
                {languages.map((lang) => {
                  const currentVal =
                    globalDictionary[originalText]?.[lang] || "";
                  return (
                    <Form.Field key={lang}>
                      <label>Translation ({lang})</label>
                      <Input
                        placeholder={
                          originalText?.trim() === ""
                            ? `Translation for empty string`
                            : `Translation for "${originalText}"`
                        }
                        value={currentVal}
                        onChange={(e) =>
                          handleTranslationChange(
                            originalText,
                            lang,
                            e.target.value
                          )
                        }
                      />
                    </Form.Field>
                  );
                })}
              </div>
            </Segment>
          );
        })}
      </Form>
    </div>
  );
}

/* ==========================================================
   3) LanguagePane
   - For each language tab, show questions/factors/aggregates
     in context, referencing the global dictionary.
   ========================================================== */
function LanguagePane({
  lang,
  questions = [],
  factors = [],
  factoAggregate = [],
  globalDictionary,
}) {
  // Accordion states
  const [activeIndexQ, setActiveIndexQ] = useState(null);
  const [activeIndexF, setActiveIndexF] = useState(null);
  const [activeIndexA, setActiveIndexA] = useState(null);

  // Handlers for opening/closing accordions
  const handleQClick = (e, titleProps) => {
    const { index } = titleProps;
    setActiveIndexQ(activeIndexQ === index ? null : index);
  };
  const handleFClick = (e, titleProps) => {
    const { index } = titleProps;
    setActiveIndexF(activeIndexF === index ? null : index);
  };
  const handleAClick = (e, titleProps) => {
    const { index } = titleProps;
    setActiveIndexA(activeIndexA === index ? null : index);
  };

  // "t()" tries to get a translated version from the dictionary
  const t = (original) => {
    if (typeof original === "undefined") return "";
    const dictEntry = globalDictionary[original];
    if (dictEntry && dictEntry[lang]) {
      return dictEntry[lang];
    }
    // fallback
    return original;
  };

  // Render blocks (for consent or information items, or any item that has blocks)
  const renderBlocks = (blocks) => {
    if (!Array.isArray(blocks) || !blocks.length) return null;
    return (
      <Segment secondary style={{ marginTop: 10 }}>
        <Header as="h5">Blocks</Header>
        {blocks.map((block, idx) => {
          const originalContent = block.content ?? "";
          const originalLabel = block.label ?? "";
          return (
            <div key={idx} style={{ marginBottom: "1em" }}>
              <strong>Block type:</strong> {block.type}
              {block.level && <> (level: {block.level})</>}

              {/* If there's a "content" field */}
              {typeof block.content !== "undefined" && (
                <Form.Field style={{ marginTop: "0.5em" }}>
                  <label>Content</label>
                  <Label basic color="blue">
                    Original: {originalContent || "(empty)"}
                  </Label>
                  <Input value={t(originalContent)} readOnly />
                </Form.Field>
              )}

              {/* If there's a "label" field (e.g. checkbox) */}
              {typeof block.label !== "undefined" && (
                <Form.Field style={{ marginTop: "0.5em" }}>
                  <label>Label</label>
                  <Label basic color="blue">
                    Original: {originalLabel || "(empty)"}
                  </Label>
                  <Input value={t(originalLabel)} readOnly />
                </Form.Field>
              )}
            </div>
          );
        })}
      </Segment>
    );
  };

  // Render Questions
  const renderQuestions = () => {
    return questions.map((q, i) => {
      const p = q.properties || {};
      const conds = q.conditionals?.conditionals || [];

      return (
        <Accordion styled key={q.uniqueId}>
          <Accordion.Title
            active={activeIndexQ === i}
            index={i}
            onClick={handleQClick}
          >
            <Icon name="dropdown" />
            {`Question: ${q.uniqueId} (${q.type})`}
          </Accordion.Title>
          <Accordion.Content active={activeIndexQ === i}>
            {/* Basic label */}
            {typeof p.label !== "undefined" && (
              <Form.Field>
                <label>Label</label>
                <Label basic color="blue">
                  Original: {p.label || "(empty)"}
                </Label>
                <Input value={t(p.label)} readOnly />
              </Form.Field>
            )}

            {/* lowLabel/highLabel (for likert, etc.) */}
            {typeof p.lowLabel !== "undefined" && (
              <Form.Field>
                <label>Low Label</label>
                <Label basic color="blue">
                  Original: {p.lowLabel || "(empty)"}
                </Label>
                <Input value={t(p.lowLabel)} readOnly />
              </Form.Field>
            )}
            {typeof p.highLabel !== "undefined" && (
              <Form.Field>
                <label>High Label</label>
                <Label basic color="blue">
                  Original: {p.highLabel || "(empty)"}
                </Label>
                <Input value={t(p.highLabel)} readOnly />
              </Form.Field>
            )}

            {/* options */}
            {Array.isArray(p.options) && p.options.length > 0 && (
              <>
                <Header as="h5" style={{ marginTop: 10 }}>
                  Options
                </Header>
                {p.options.map((opt, idx) => (
                  <Form.Field key={idx}>
                    <Label basic color="blue">
                      Original: {opt || "(empty)"}
                    </Label>
                    <Input value={t(opt)} readOnly />
                  </Form.Field>
                ))}
              </>
            )}

            {/* conditionals */}
            {conds.length > 0 && (
              <Segment secondary style={{ marginTop: 10 }}>
                <Header as="h5">Conditionals</Header>
                {conds.map((cond) => (
                  <div key={cond.id} style={{ marginBottom: "1em" }}>
                    <strong>ID: {cond.id}</strong> &nbsp;
                    <em>({cond.operator} {cond.threshold})</em>
                    {/* followupText */}
                    {typeof cond.followupText !== "undefined" && (
                      <Form.Field style={{ marginTop: "0.5em" }}>
                        <label>Follow-Up Text</label>
                        <Label basic color="blue">
                          Original: {cond.followupText || "(empty)"}
                        </Label>
                        <Input value={t(cond.followupText)} readOnly />
                      </Form.Field>
                    )}
                    {/* aiPrompt */}
                    {typeof cond.aiPrompt !== "undefined" && (
                      <Form.Field style={{ marginTop: "0.5em" }}>
                        <label>AI Prompt</label>
                        <Label basic color="blue">
                          Original: {cond.aiPrompt || "(empty)"}
                        </Label>
                        <Input value={t(cond.aiPrompt)} readOnly />
                      </Form.Field>
                    )}
                  </div>
                ))}
              </Segment>
            )}

            {/* blocks (for consent or information content) */}
            {renderBlocks(p.blocks)}
          </Accordion.Content>
        </Accordion>
      );
    });
  };

  // Render Factors
  const renderFactors = () => {
    return factors.map((factor, i) => {
      const { factorId, title, description, followUps } = factor;

      return (
        <Accordion styled key={factorId}>
          <Accordion.Title
            active={activeIndexF === i}
            index={i}
            onClick={handleFClick}
          >
            <Icon name="dropdown" />
            Factor: {factorId}
          </Accordion.Title>
          <Accordion.Content active={activeIndexF === i}>
            {/* title */}
            {typeof title !== "undefined" && (
              <Form.Field>
                <label>Title</label>
                <Label basic color="blue">
                  Original: {title || "(empty)"}
                </Label>
                <Input value={t(title)} readOnly />
              </Form.Field>
            )}
            {/* description */}
            {typeof description !== "undefined" && (
              <Form.Field>
                <label>Description</label>
                <Label basic color="blue">
                  Original: {description || "(empty)"}
                </Label>
                <TextArea value={t(description)} readOnly />
              </Form.Field>
            )}

            {/* followUps */}
            {Array.isArray(followUps) && followUps.length > 0 && (
              <Segment secondary style={{ marginTop: 10 }}>
                <Header as="h5">Factor Follow-Ups</Header>
                {followUps.map((fu) => (
                  <div key={fu.followUpId} style={{ marginBottom: "1em" }}>
                    <strong>FollowUp: {fu.followUpId}</strong>
                    {typeof fu.aiPrompt !== "undefined" && (
                      <Form.Field style={{ marginTop: "0.5em" }}>
                        <label>AI Prompt</label>
                        <Label basic color="blue">
                          Original: {fu.aiPrompt || "(empty)"}
                        </Label>
                        <TextArea value={t(fu.aiPrompt)} readOnly />
                      </Form.Field>
                    )}
                  </div>
                ))}
              </Segment>
            )}
          </Accordion.Content>
        </Accordion>
      );
    });
  };

  // Render factoAggregate
  const renderAggregateFactors = () => {
    return factoAggregate.map((agg, i) => {
      const { followUpId, followUpQuestion, aiPrompt } = agg;
      return (
        <Accordion styled key={followUpId}>
          <Accordion.Title
            active={activeIndexA === i}
            index={i}
            onClick={handleAClick}
          >
            <Icon name="dropdown" />
            Aggregate Factor: {followUpId}
          </Accordion.Title>
          <Accordion.Content active={activeIndexA === i}>
            {typeof followUpQuestion !== "undefined" && (
              <Form.Field>
                <label>Follow-Up Question</label>
                <Label basic color="blue">
                  Original: {followUpQuestion || "(empty)"}
                </Label>
                <TextArea value={t(followUpQuestion)} readOnly />
              </Form.Field>
            )}
            {typeof aiPrompt !== "undefined" && (
              <Form.Field>
                <label>AI Prompt</label>
                <Label basic color="blue">
                  Original: {aiPrompt || "(empty)"}
                </Label>
                <TextArea value={t(aiPrompt)} readOnly />
              </Form.Field>
            )}
          </Accordion.Content>
        </Accordion>
      );
    });
  };

  return (
    <Form style={{ padding: 20 }}>
      <Header as="h3">
        Translations for <em>{lang}</em>
      </Header>

      <Header as="h4" dividing>
        Questions / Items
      </Header>
      {renderQuestions()}

      <Header as="h4" dividing style={{ marginTop: "2em" }}>
        Factors
      </Header>
      {renderFactors()}

      <Header as="h4" dividing style={{ marginTop: "2em" }}>
        Aggregate Factors
      </Header>
      {renderAggregateFactors()}
    </Form>
  );
}

/* ==========================================================
   4) Main Export: LanguagesTab
   - Builds unique strings
   - Renders "Global Strings" tab + one tab per language
   ========================================================== */
export default function LanguagesTab({
  questions,
  factors,
  factoAggregate,
  languageCodes,       // parent's array of languages
  setLanguageCodes,    // parent's setter for languages
  globalDictionary,    // parent's dictionary object
  setGlobalDictionary, // parent's setter for the dictionary
}) {
  // Gather unique strings on mount or data change
  const [allStrings, setAllStrings] = useState([]);

  // Supported languages to pick from (hard-coded list).
  const SUPPORTED_LANG_CODES = ["FR", "ES", "POR", "GER", "TUR"];

  useEffect(() => {
    const unique = extractUniqueStrings(questions, factors, factoAggregate);
    setAllStrings(unique);
  }, [questions, factors, factoAggregate]);

  // For adding new languages
  const [newLang, setNewLang] = useState("");

  // Create a list of dropdown options for languages that haven't been added yet
  const availableLangOptions = SUPPORTED_LANG_CODES.filter(
    (code) => !languageCodes.includes(code)
  ).map((code) => ({
    key: code,
    value: code,
    text: code,
  }));

  const handleAddLanguage = () => {
    if (newLang && !languageCodes.includes(newLang)) {
      setLanguageCodes((prev) => [...prev, newLang]);
    }
    setNewLang("");
  };

  // Build the tab panes
  const panes = [
    {
      menuItem: "Global Strings",
      render: () => (
        <Tab.Pane>
          <GlobalStringsTab
            allStrings={allStrings}
            languages={languageCodes}
            globalDictionary={globalDictionary}
            setGlobalDictionary={setGlobalDictionary}
          />
        </Tab.Pane>
      ),
    },
    ...languageCodes.map((lang) => ({
      menuItem: lang,
      render: () => (
        <Tab.Pane>
          <LanguagePane
            lang={lang}
            questions={questions}
            factors={factors}
            factoAggregate={factoAggregate}
            globalDictionary={globalDictionary}
          />
        </Tab.Pane>
      ),
    })),
  ];

  return (
    <div style={{ margin: 20 }}>
      <Header as="h2" dividing>
        Manage Survey Translations
      </Header>

      {/* Add Language UI */}
      <Form>
        <Form.Group inline>
          <Form.Field width={4}>
            <label>Select a Language</label>
            <Dropdown
              placeholder="Choose a Language"
              fluid
              selection
              options={availableLangOptions}
              value={newLang}
              onChange={(e, data) => setNewLang(data.value)}
            />
          </Form.Field>
          <Form.Field>
            <Button primary onClick={handleAddLanguage} disabled={!newLang}>
              <Icon name="plus" />
              Add
            </Button>
          </Form.Field>
        </Form.Group>
      </Form>

      {/* Display current languages */}
      {languageCodes.length > 0 ? (
        <p>
          <strong>Current Languages:</strong> {languageCodes.join(", ")}
        </p>
      ) : (
        <p>No languages added yet.</p>
      )}

      {/* Semantic UI Tab */}
      {languageCodes.length > 0 && (
        <Tab
          menu={{ secondary: true, pointing: true }}
          panes={panes}
          defaultActiveIndex={0}
        />
      )}
    </div>
  );
}
